// Pure front-end course loading.
// Courses are static markdown files under `public/courses/` and are parsed
// entirely in the browser — no backend API is required for course content.
// This mirrors the parsing logic that previously lived in backend/server.py.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Base path for static assets (respects `homepage` / PUBLIC_URL on static hosts).
const PUBLIC_BASE = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const COURSES_BASE = `${PUBLIC_BASE}/courses`;

const META_KEYS = ["id", "title", "category", "difficulty", "order", "description"];

// Parse a single markdown file into a full course object.
const parseCourse = (text, fallbackId) => {
  const meta = {};
  let body = text;

  const m = text.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (m) {
    const [, fm, rest] = m;
    body = rest;
    fm.split("\n").forEach((line) => {
      const idx = line.indexOf(":");
      if (idx !== -1) {
        const k = line.slice(0, idx).trim();
        const v = line.slice(idx + 1).trim();
        if (k) meta[k] = v;
      }
    });
  }

  // Extract the LAST fenced block for a given language (matches backend).
  const extract = (lang) => {
    const re = new RegExp("```" + lang + "\\b[^\\n]*\\n([\\s\\S]*?)```", "g");
    let last = "";
    let match;
    while ((match = re.exec(body)) !== null) last = match[1];
    return last.replace(/\n+$/, "");
  };

  let order = parseInt(meta.order, 10);
  if (Number.isNaN(order)) order = 999;

  return {
    id: meta.id || fallbackId,
    title: meta.title || fallbackId,
    category: meta.category || "其他",
    difficulty: meta.difficulty || "初级",
    order,
    description: meta.description || "",
    markdown: body.trim(),
    code: {
      html: extract("html"),
      css: extract("css"),
      js: extract("js"),
    },
  };
};

let _coursesPromise = null;

const loadAllCourses = () => {
  if (_coursesPromise) return _coursesPromise;
  _coursesPromise = (async () => {
    const manifestRes = await fetch(`${COURSES_BASE}/manifest.json`);
    console.log("Fetching manifest...", manifestRes.status, manifestRes.statusText);
    if (!manifestRes.ok) throw new Error("manifest not found");
    const { files } = await manifestRes.json();

    console.log("Loading courses from manifest:", files.length);

    const parsed = await Promise.all(
      files.map(async (file) => {
        const res = await fetch(`${COURSES_BASE}/${file}`);
        if (!res.ok) throw new Error(`failed to load ${file}`);
        const text = await res.text();
        return parseCourse(text, file.replace(/\.md$/, ""));
      })
    );

    parsed.sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
    return parsed;
  })();
  // Reset cache on failure so a later retry can re-attempt.
  _coursesPromise.catch(() => {
    _coursesPromise = null;
  });
  return _coursesPromise;
};

export const fetchCourses = async () => {
  const courses = await loadAllCourses();
  console.log("Loaded courses:", courses.length);
  return courses.map((c) => {
    const meta = {};
    META_KEYS.forEach((k) => (meta[k] = c[k]));
    return meta;
  });
};

export const fetchCourse = async (id) => {
  const courses = await loadAllCourses();
  const course = courses.find((c) => c.id === id);
  if (!course) throw new Error("Course not found");
  return course;
};

// Source archive is a static asset generated at build time (scripts/build-source-zip.js).
export const downloadSourceUrl = `${COURSES_BASE.replace(/\/courses$/, "")}/source.zip`;
